import config from 'app-config'
import { useMemo, useCallback, useEffect, useReducerState } from 'sb-hooks'


type WidgetIdsType = {
  walletWidgetId: string
  consentWidgetId: string
  addressBookWidgetId: string
}

type OptionsType = {
  widgetIds: WidgetIdsType
  onAddressSelect: (orderReferenceId: string) => Promise<void>
}

type ReturnType = {
  areWidgetsValid: boolean
  amazonPayBillingAgreementId: string
}


const useAmazonPayWidgets = ({ widgetIds, onAddressSelect }: OptionsType): ReturnType => {
  const { walletWidgetId, consentWidgetId, addressBookWidgetId } = widgetIds

  const [ state, setState ] = useReducerState({
    amazonPayBillingAgreementId: null,
    orderReferenceId: null,
    isConsentSelected: false,
    isWalletSelected: false,
    shouldFetchAddress: false,
  })

  const {
    amazonPayBillingAgreementId, orderReferenceId,
    isConsentSelected, isWalletSelected, isAddressSelected, shouldFetchAddress,
  } = state

  useEffect(() => {
    if (shouldFetchAddress && orderReferenceId) {
      onAddressSelect(orderReferenceId)
        .then(() => setState({ shouldFetchAddress: false }))
    }
  }, [ onAddressSelect, shouldFetchAddress, orderReferenceId ])

  const areWidgetsValid = useMemo(() => (
    !amazonPayBillingAgreementId
    || !isConsentSelected
    || !isWalletSelected
    || !isAddressSelected
  ), [
    amazonPayBillingAgreementId,
    isConsentSelected,
    isWalletSelected,
    isAddressSelected,
  ])

  const setConsentStatus = useCallback((billingAgreementConsentStatus) => {
    if (billingAgreementConsentStatus.type === 'Consent') {
      const status = billingAgreementConsentStatus.getConsentStatus()

      // Amazon returns status true/false as string
      const isConsentSelected = typeof status === 'string'
        ? status === 'true'
        : status

      setState({ isConsentSelected })
    }
  }, [])

  const addConsentWidget = useCallback((amazonBillingAgreementId) => {
    new window.OffAmazonPayments.Widgets.Consent({
      sellerId: config.publicKeys.amazonPaySellerId,
      amazonBillingAgreementId,
      design: {
        designMode: 'responsive',
      },
      onReady: setConsentStatus,
      onConsent: setConsentStatus,
    })
      .bind(consentWidgetId)
  }, [])

  const commonWidgetsProps = useMemo(() => ({
    sellerId: config.publicKeys.amazonPaySellerId,
    agreementType: 'BillingAgreement',
    design: {
      designMode: 'responsive',
    },
  }), [])

  const addWalletWidget = useCallback((amazonBillingAgreementId) => {
    new window.OffAmazonPayments.Widgets.Wallet({
      ...commonWidgetsProps,
      amazonBillingAgreementId,
      onReady: () => addConsentWidget(amazonBillingAgreementId),
      onPaymentSelect: () => setState({ isWalletSelected: true }),
    })
      .bind(walletWidgetId)
  }, [])

  const addAddressBookWidget = useCallback(() => {
    new window.OffAmazonPayments.Widgets.AddressBook({
      ...commonWidgetsProps,
      onOrderReferenceCreate: (orderReference) => {
        const orderReferenceId = orderReference.getAmazonOrderReferenceId()

        setState({ orderReferenceId })
      },
      onAddressSelect: () => setState({ isAddressSelected: true, shouldFetchAddress: true }),
      onReady: (billingAgreement) => {
        const amazonPayBillingAgreementId = billingAgreement.getAmazonBillingAgreementId()

        setState({ amazonPayBillingAgreementId })
        addWalletWidget(amazonPayBillingAgreementId)
      },
    })
      .bind(addressBookWidgetId)
  }, [])

  useEffect(() => {
    addAddressBookWidget()
  }, [])

  return {
    areWidgetsValid,
    amazonPayBillingAgreementId,
  }
}


export default useAmazonPayWidgets
